<template>
  <div class="auth admin">
    <template v-if="state !== 'signed-in'">
      <h3>{{ t('admin-title') }}</h3>
      <form v-if="state === 'signed-out'" novalidate @submit.prevent="signIn">
        <label>{{ t('field-admin-password') }}<input v-model="password" type="password" name="adminPassword" autocomplete="current-password" /></label>
        <p v-if="error" class="warn">{{ error }}</p>
        <button type="submit" :disabled="busy">{{ t('button-sign-in') }}</button>
      </form>
    </template>

    <template v-else>
      <div class="admin-top">
        <h3>{{ t('app-name') }} {{ t('admin-title') }}</h3>
        <button class="admin-sign-out" type="button" @click="signOut">{{ t('admin-sign-out') }}</button>
      </div>
      <div class="admin-tabs" role="tablist">
        <button v-for="tab in shownTabs" :key="tab.key" type="button" role="tab" :data-tab="tab.key"
                :aria-selected="tab.key === current ? 'true' : 'false'" :class="{ on: tab.key === current }"
                @click="go({ tab: tab.key })">
          {{ t(tab.title) }}<i v-if="counts[tab.key] !== undefined">{{ counts[tab.key] }}</i>
        </button>
      </div>
      <div class="admin-card">
        <AdminGames v-show="current === 'games'" :player="player" @games="games = $event; counts.games = $event.length"
                    @clear-player="go({ tab: 'games' })" />
        <AdminAccounts v-show="current === 'accounts'" :games="games" @count="counts.accounts = $event"
                       @show-games="go({ tab: 'games', player: $event })" />
        <component :is="s.component" v-for="s in sections" v-show="current === s.key" :key="s.key"
                   @count="$set(counts, s.key, $event)" />
      </div>
    </template>
  </div>
</template>

<script>
// The admin zone (/admin, SPEC §4.13), reached only by its address. Its parts are tabs, chosen by the
// address (?tab=games|accounts|…, and for games &player=<id>), so the browser's back button and a
// reload keep the view (docs/plans/2026-10-22-admin-page.md). Modules add tabs through
// extensions.adminSections ({ key, title, component }); a module's tab shows once its part emits
// `count`, so a module that isn't set up shows none.
import AdminGames from './admin/AdminGames';
import AdminAccounts from './admin/AdminAccounts';
import extensions from '@/extensions';
import { t, errorText } from '@/texts';
import { adminCall, rememberedAdminSession, rememberAdminSession } from '@/admin';

export default {
  name: 'AdminPage',
  components: { AdminGames, AdminAccounts },
  data() {
    return { state: null, password: '', error: null, busy: false, sections: extensions.adminSections,
             games: null, counts: { games: undefined, accounts: undefined } };
  },
  computed: {
    tabs() {
      return [{ key: 'games', title: 'admin-games' }, { key: 'accounts', title: 'admin-accounts' }, ...this.sections];
    },
    shownTabs() {
      return this.tabs.filter(tab => tab.key === 'games' || tab.key === 'accounts' || this.counts[tab.key] !== undefined);
    },
    current() {
      const tab = this.$route.query.tab;
      return this.tabs.some(x => x.key === tab) ? tab : 'games';
    },
    player() {
      const id = Number(this.$route.query.player);
      return this.current === 'games' && id > 0 ? id : null;
    },
  },
  async created() {
    const id = rememberedAdminSession();
    if (id) {
      try {
        await adminCall('GET', `admin-sessions/${id}`);
        this.state = 'signed-in';
        return;
      } catch (e) {
        rememberAdminSession(null);
      }
    }
    this.state = 'signed-out';
  },
  methods: {
    t,
    go(query) {
      const q = { tab: query.tab };
      if (query.player) q.player = String(query.player);
      if (q.tab === this.$route.query.tab && q.player === this.$route.query.player) return;
      this.$router.push({ query: q });
    },
    async signIn() {
      this.error = null;
      this.busy = true;
      try {
        const s = await adminCall('POST', 'admin-sessions', { password: this.password });
        rememberAdminSession(s.id);
        this.password = '';
        this.state = 'signed-in';
      } catch (e) {
        this.error = errorText(e.msg || String(e));
      } finally {
        this.busy = false;
      }
    },
    async signOut() {
      const id = rememberedAdminSession();
      try { if (id) await adminCall('POST', `admin-sessions/${id}/sign-out`); } catch (e) { /* already ended */ }
      rememberAdminSession(null);
      this.state = 'signed-out';
    },
  },
};
</script>

<style src="./auth.css"></style>
<style src="./admin/admin.css"></style>
